import { Sport } from './sports.model';
import type { WeeklyScheduleSlot } from './tournament-round.model';

export const TO_BE_DEFINED_LABEL = 'A definir';

/** API / tournament: no minimum age floor (unrestricted below). */
export const TOURNAMENT_AGE_MIN_UNRESTRICTED = 0;
/** API / tournament: default no maximum age cap (unrestricted above). */
export const TOURNAMENT_AGE_MAX_UNRESTRICTED = 200;

export enum STEP_CREATE_TOURNAMENT {
  SPORT_SELECTION = 1,
  TOURNAMENT_DETAILS = 2,
  COURT_SELECTION = 3,
  SCHEDULE_SELECTION = 4,
  CONFIRMATION_PAGE = 5,
}
export enum Walltype {
  GLASS = 'GLASS',
  CONCRETE = 'CONCRETE',
}

export enum Surface {
  ARTIFICIAL_GRASS_SAND = 'ARTIFICIAL_GRASS_SAND',
  CONCRETE = 'CONCRETE',
  POROUS_CONCRETE = 'POROUS_CONCRETE',
  SYNTHETIC_RESIN = 'SYNTHETIC_RESIN',
}

export enum TournamentType {
  STRICT = 'STRICT',
  SOFT = 'SOFT',
}

export enum TournamentStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
}

export const CATEGORY_RANKING_TARGET = {
  PLAYER_CATEGORY: 'PLAYER_CATEGORY',
  FIXED_CATEGORY: 'FIXED_CATEGORY',
} as const;

export type CategoryRankingTarget =
  (typeof CATEGORY_RANKING_TARGET)[keyof typeof CATEGORY_RANKING_TARGET];

export enum PaymentType {
  MERCADOPAGO = 'MERCADO_PAGO',
  CASH = 'CASH',
  ALL = 'ANY',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  MIXED = 'MIXED',
  ANY = 'ANY',
}

export type Court = {
  id: string;
  sport: Sport;
  name: string;
  surface: Surface;
  wallType: Walltype;
  price: Price;
  clubId: string;
  hasRoof: boolean;
};

export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export type Currency = {
  isoCode: string;
  name: string;
};

export type Price = {
  id: string;
  value: number;
  currency: Currency;
};

export type Category = {
  id: number;
  minimumElo: number;
  name: string;
  sport: Sport;
};

export type Club = {
  id: string;
  name: string;
  status: string;
  address: {
    id: string;
    zipCode: string;
    street: string;
    state: string;
    city: string;
    country: {
      isoCode: string;
      name: string;
      flag2: string;
      phonePrefix: string;
    };
    timezone?: string;
  };
  cellphone?: string;
  instagramLink?: string;
  facebookLink?: string;
  courts: Court[];
  availability: Availability[];
  schedules: Availability[];
  cuit?: string;
  bannerUrl?: string | null;
};

export type TimeRange = {
  qty: number;
  courtsQuantity: number;
  startTime: string;
  endTime: string;
};

export type Availability = {
  date: string;
  dayOfWeek: DayOfWeek;
  timeRange: TimeRange[];
};

export type TournamentFormData = {
  name: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  signupDeadlineDate: Date | null;
  matchDuration: number; // Duration in minutes
  /**
   * Minimum individual time-slots a player must select at registration.
   * null = not set (backend defaults to 1).
   */
  minSchedulesRequired?: number | null;
  courts?: Court[]; // Optional - not required for tournament creation
  clubs: Club[];
  schedule: FormSchedule[] | Array<{ id: string }>; // Can be FormSchedule[] or array of schedule IDs
  // Additional fields needed for API
  bannerUrl: string;
  sport: Sport;
  price: Price;
  type: TournamentType;
  gender: Gender;
  minCategory: Category;
  maxCategory: Category;
  minTeamsAllowed: number;
  maxTeamsAllowed: number;
  inPause: boolean;
  forceSum: boolean;
  /** Whether the tournament awards points to the club's category ranking. Default true. */
  countsForCategoryRanking?: boolean;
  /** Where the points go: each player's own category or one fixed category. Default PLAYER_CATEGORY. */
  categoryRankingTarget?: CategoryRankingTarget;
  /** Only sent when categoryRankingTarget is FIXED_CATEGORY. */
  categoryRankingCategory?: Category;
  playersPerTeam: number;
  prizes: string;
  paymentType: PaymentType;
  teamsPerGroups: number;
  classifiedPerGroups: number;
  fixtureType: FixtureType;
  /** SHORT | AMERICAN | LONG — API field `format`. */
  format: TournamentFormat;
  /** When groups of 4 in formato corto: allow 3 classified per group vs standard. */
  overflowGroupClassification?: OverflowGroupClassification;
  /** Final knockout bracket size for AMERICAN tournaments. */
  americanFinalBracketSize?: AmericanFinalBracketSize;
  /** Group format for AMERICAN tournaments. Default: WINNER_LOSER. */
  americanGroupFormat?: AmericanGroupFormat;
  minAge: number;
  maxAge: number;
  // Club ID for schedule selection mode (optional)
  clubId?: string | null;
  // Configuration fields for the LONG format (all optional)
  // minSchedulesRequired is already declared above (shared with the other formats)
  availabilityDeadlineDay?: string | null;
  availabilityDeadlineTime?: string | null;
  walkoversForDisqualification?: number | null;
  autoPublishRounds?: boolean;
  /** Weekly schedule pattern for LONG tournaments. Only sent when format === LONG. */
  weeklyScheduleTemplate?: WeeklyScheduleSlot[];
};

export type CourtSelectionProps = {
  formData: TournamentFormData;
  clubs: Club[];
  onConfirm: (selectedClub: Club) => void;
  onPrev: () => void;
};

export type ScheduleSelectionProps = {
  court: Court;
  onConfirm: (updatedCourt: Court) => void;
  onPrev: () => void;
  onTimeSlotClick?: (date: string, hour: string, count: number) => void;
};

export type SportSelectionProps = {
  formData: TournamentFormData;
  setFormData: React.Dispatch<React.SetStateAction<TournamentFormData>>;
  onNext: () => void;
};

export type TournamentDetailsProps = {
  formData: TournamentFormData;
  setFormData: React.Dispatch<React.SetStateAction<TournamentFormData>>;
  onNext: () => void;
  onSelectCourts: () => void;
  onPrev: () => void;
};

export type ConfirmationPageProps = {
  formData: TournamentFormData;
  tournamentId: string;
  onReset: () => void;
};

export type ScheduleHeaderProps = {
  onPrev: () => void;
};

export type TournamentResponse = {
  content: Tournament[];
};

export type Tournament = {
  id: string;
  name: string;
  description: string;
  prizes: string;
  bannerUrl: string;
  sport: Sport;
  price: Price;
  startDate: string;
  endDate: string;
  signupDeadlineDate: string;
  type: TournamentType;
  gender: Gender;
  paymentType: PaymentType;
  minCategory: Category;
  maxCategory: Category;
  minTeamsAllowed: number;
  maxTeamsAllowed: number;
  status: TournamentStatus;
  inPause: boolean;
  forceSum: boolean;
  /** Whether the tournament awards points to the club's category ranking. */
  countsForCategoryRanking?: boolean;
  categoryRankingTarget?: CategoryRankingTarget;
  categoryRankingCategory?: Category;
  teams: Team[];
  fixtureType: FixtureType;
  /** True when a fixture (draft or active) already exists. Absent when not requested. */
  hasFixture?: boolean;
  /** True when the fixture exists and its status is ACTIVE. Absent when not requested. */
  hasActiveFixture?: boolean;
  /** Bracket with no group stage (SHORT + KNOCKOUTS), as the backend computes it. Absent when not requested. */
  isKnockoutOnly?: boolean;
  /** Teams the backend needs to generate the fixture (never under 2 for knockout-only). Absent when not requested. */
  minTeamsToGenerate?: number;
  /**
   * Fewer COMPLETE registrations than {@link minTeamsToGenerate}: cancelling then refunds every payment,
   * cash included, instead of leaving the cash fee as debt. Absent when not requested.
   */
  isBelowMinimumTeams?: boolean;
  /**
   * Knockout-only: whether the bracket can use every day of the schedule. Sharing a block with a groups
   * tournament keeps it on the last day. Absent when not requested.
   */
  knockoutUsesEveryDay?: boolean;
  /** Knockout cut (club-local date `YYYY-MM-DD`). Absent unless requested, and when there is no cut. */
  knockoutStartDate?: string | null;
  /** Knockout cut (club-local time `HH:mm`). Absent unless requested, and when there is no cut. */
  knockoutStartTime?: string | null;
  /** Present when API returns tournament `format` (SHORT | AMERICAN | LONG). */
  format?: TournamentFormat;
  americanFinalBracketSize?: AmericanFinalBracketSize;
  overflowGroupClassification?: OverflowGroupClassification;
  americanGroupFormat?: AmericanGroupFormat;
  registrations: Registration[];
  clubs: Club[];
  schedule: Schedule[];
  /**
   * Close of the tournament's last scheduled match — the final, in any format with a bracket.
   * Absent while the tournament has no fixture, or while every match is still provisional.
   */
  endsAt?: string | null;
  matchDuration: number; // Duration in minutes
  /**
   * Minimum individual time-slots a player must select at registration.
   * null / absent = backend defaults to 1.
   */
  minSchedulesRequired?: number | null;
  playersPerTeam: number;
  teamsPerGroups: number;
  classifiedPerGroups: number;
  timezone: string;
  getmatchFee: number;
  minAge?: number;
  maxAge?: number;
  // Configuration fields for the LONG format (all optional)
  // minSchedulesRequired is already declared above (shared with the other formats)
  availabilityDeadlineDay?: string | null; // 'MONDAY' | 'TUESDAY' | ...
  availabilityDeadlineTime?: string | null; // 'HH:mm'
  walkoversForDisqualification?: number | null;
  autoPublishRounds?: boolean;
};

export type Schedule = {
  club: Club;
  schedule: Availability[];
  /** Present when the API exposes reusable schedule templates */
  id?: string;
  name?: string;
  description?: string;
  knockoutStartDate?: string | null;
  knockoutStartTime?: string | null;
};

export type FormSchedule = {
  club: { id: string };
  schedule: Availability[];
};

export type Team = {
  id: string;
  players: Player[];
};

export type Player = {
  id: string;
  email: string;
  username: string;
  name: string;
  gender: string;
  /** ISO date/datetime from API; may be empty if not set. */
  birthdate?: string;
  cellphone: string;
  nationality: {
    isoCode: string;
    name: string;
    flag2: string;
    phonePrefix: string;
  };
  rankings: Ranking[];
  profileImageUrl?: string;
};

export type CategoryChangeBlockReason =
  | 'MANAGER_IS_THE_PLAYER'
  | 'PLAYED_LATER_WITH_OTHER_MANAGER'
  | 'NO_ELO_IN_SPORT';

export type CategoryChangeEligibility = {
  playerEmail: string;
  eligible: boolean;
  reason: CategoryChangeBlockReason | null;
};

export type Ranking = {
  elo: number;
  sport: Sport;
};

export enum FixtureType {
  KNOCKOUTS = 'KNOCKOUTS',
  GROUPS_AND_KNOCKOUTS = 'GROUPS_AND_KNOCKOUTS',
  GROUPS_ONLY = 'GROUPS_ONLY',
}

/**
 * Tournament-level match format (API field `format`).
 * Not to be confused with {@link FixtureType} (groups vs knockouts structure).
 */
export enum TournamentFormat {
  SHORT = 'SHORT',
  AMERICAN = 'AMERICAN',
  LONG = 'LONG',
}

/**
 * Size of the final knockout bracket in an American tournament (API `americanFinalBracketSize`).
 */
export enum AmericanFinalBracketSize {
  NONE = 'NONE',
  FINAL = 'FINAL',
  FOUR = 'FOUR',
  EIGHT = 'EIGHT',
}

/**
 * Group phase tie-break / extra qualifier for groups of 4 (API `overflowGroupClassification`).
 */
export enum OverflowGroupClassification {
  STANDARD = 'STANDARD',
  EXTRA_CLASSIFIER = 'EXTRA_CLASSIFIER',
}

/**
 * Group format for AMERICAN tournaments (API `americanGroupFormat`).
 * WINNER_LOSER = 2+2 bracket (default): semis → ganador vs ganador, perdedor vs perdedor.
 * ROUND_ROBIN = todos contra todos.
 */
export enum AmericanGroupFormat {
  WINNER_LOSER = 'WINNER_LOSER',
  ROUND_ROBIN = 'ROUND_ROBIN',
}

export enum FixtureStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
}

export type Fixture = {
  groupList?: Group[];
  knockoutTree?: KnockoutTree;
  status?: FixtureStatus;
};

export type TeamWithStats = {
  team: Team;
  stats: {
    matchesPlayed: number;
    wins: number;
    draws: number;
    losses: number;
    setsWon: number;
    setsLost?: number;
    gamesWon: number;
    gamesLost?: number;
    totalPoints: number;
  };
};

export type Group = {
  teamList: TeamWithStats[];
  matchList: Match[];
};

export type KnockoutTree = {
  rootNode: KnockoutNode;
};

export type KnockoutNode = {
  parent?: KnockoutNode;
  leftChild?: KnockoutNode;
  rightChild?: KnockoutNode;
  match?: Match;
  advancingTeam?: Team;
  round: number;
  roundName?: string;
};

export enum MatchType {
  TOURNAMENT = 'TOURNAMENT',
  COMPETITIVE = 'COMPETITIVE',
  FRIENDLY = 'FRIENDLY',
}

export enum ForcedReason {
  /** Más partidos que canchas en esa franja. Jugable si el club suma una cancha. */
  OVERBOOKED = 'OVERBOOKED',
  /** Horario provisional: hay que reagendarlo antes de activar. */
  UNSCHEDULED = 'UNSCHEDULED',
}

export type Match = {
  id: string;
  team1?: Team;
  team2?: Team;
  result?: MatchResult;
  startDate: string;
  endDate: string;
  playersPerTeam: number;
  sport: Sport;
  matchType: MatchType;
  gender: Gender;
  competitiveMatchType: TournamentType;
  club?: Club;
  location?: string;
  createdBy?: string;
  category?: Category;
  /** LONG tournaments: false = proposed but not published yet; true = scheduled/published. */
  scheduled?: boolean;
  pendingReschedule?: boolean;
  /** LONG tournaments: true = the slot was forced because team availability didn't match. */
  conflict?: boolean;
  /** LONG tournaments: forced because a team didn't load availability (softer warning). */
  conflictMissingAvailability?: boolean;
  forcedReason?: ForcedReason | null;
  /**
   * Backend marker for matches whose teams come from another match's result:
   * `DEPENDENT_MATCH:<matchAId>:<matchBId>[:<roleA>:<roleB>]` while unresolved, and
   * `RESOLVED_DEPENDENT_MATCH:...` once the real teams are in. See `@/utils/dependent-match`.
   */
  description?: string;
};

/** One of the matches sitting in a slot, as returned by `/fixture/slot-occupancy`. */
export type SlotOccupant = {
  matchId: string;
  tournamentId: string;
  tournamentName: string;
  /** "Grupo B" for the group stage, the round name for the bracket. */
  stage: string;
  currentTournament: boolean;
};

/**
 * A slot of the club (one date + start time) with everything booked into it, including matches
 * from sibling tournaments that share the same courts. `courtsQuantity` is null for old blocks
 * saved before the field existed.
 */
export type SlotOccupancy = {
  startDate: string;
  courtsQuantity: number | null;
  occupants: SlotOccupant[];
};

/** A tournament whose fixture the backend generates together with another one's (they share schedule blocks). */
export type FixtureSibling = {
  id: string;
  name: string;
  /** Only with `includeNotGenerated`: why this LONG sibling is not generated; absent or null when it is. */
  notGeneratedReason?: string | null;
  /** Only with `includeToCancel`: true when this sibling is below its minimum and gets cancelled before the fixture is created. */
  toCancel?: boolean;
  /** Only on siblings with `toCancel`: how many teams are registered; null on the rest. */
  teamsRegistered?: number | null;
  /** Only on siblings with `toCancel`: the minimum of teams the tournament needs; null on the rest. */
  minTeams?: number | null;
};

/** What happened to one sibling when asking the backend to cancel it. */
export enum SiblingCancellationStatus {
  CANCELLED = 'CANCELLED',
  SKIPPED = 'SKIPPED',
  FAILED = 'FAILED',
}

/** The outcome of cancelling one sibling; `reason` explains a SKIPPED or FAILED one. */
export type FixtureSiblingCancellation = {
  id: string;
  status: SiblingCancellationStatus;
  reason?: string | null;
};

/** A tournament that appears in the shared grid. */
export type GridTournament = {
  id: string;
  name: string;
  /** The tournament the grid was entered from. */
  current: boolean;
  /** Not sent by every backend version; when absent the format is unknown. */
  format?: TournamentFormat;
};

export type GridClub = { id: string; name: string };

/** A declared cell of the grid. `courtsQuantity` null = old block with no capacity loaded. */
export type GridSlot = {
  clubId: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:mm`. */
  startTime: string;
  courtsQuantity: number | null;
};

export type GridPlayer = {
  id: string;
  name: string;
  profileImageUrl: string | null;
};

export type GridTeam = { id: string; players: GridPlayer[] };

/** A match in the grid. Availabilities come trimmed to the dates in `slots`. */
export type GridMatch = {
  matchId: string;
  tournamentId: string;
  clubId: string | null;
  /** "Grupo B" or the round name. */
  stage: string;
  startDate: string;
  endDate: string;
  timezone: string | null;
  team1: GridTeam;
  team2: GridTeam;
  /** With a result loaded, the match shows but cannot be dragged. */
  hasResult: boolean;
  forcedReason: ForcedReason | null;
  team1Availability: Availability[];
  team2Availability: Availability[];
};

/**
 * The pairs of one grid tournament (the current one or a sibling) that declared they can play at
 * a given date and time. Carries its own name because `SharedGrid.tournaments` only lists the
 * tournaments that already have matches in the grid.
 */
export type GridAvailableTeams = {
  tournamentId: string;
  name: string;
  /** The tournament the grid was entered from. */
  current: boolean;
  teams: GridTeam[];
};

export type SharedGrid = {
  tournaments: GridTournament[];
  clubs: GridClub[];
  slots: GridSlot[];
  matches: GridMatch[];
};

/** A pending move. `startTime` is local time, `YYYY-MM-DDTHH:mm:ss`. */
export type GridMove = {
  matchId: string;
  tournamentId: string;
  startTime: string;
};

export enum RegistrationStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PENDING_ADMIN_CHECK = 'PENDING_ADMIN_CHECK',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  COMPLETE = 'COMPLETE',
  REJECTED_BY_ADMIN = 'REJECTED_BY_ADMIN',
  CANCELLED_BY_PLAYERS_WITH_REFUND = 'CANCELLED_BY_PLAYERS_WITH_REFUND',
  CANCELLED_BY_PLAYERS_WITHOUT_REFUND = 'CANCELLED_BY_PLAYERS_WITHOUT_REFUND',
  CANCELLED = 'CANCELLED',
  CANCELLED_BY_PLAYERS_WITHOUT_PAY = 'CANCELLED_BY_PLAYERS_WITHOUT_PAY',
  CANCELLED_AUTOMATICALLY_BY_REFUND = 'CANCELLED_AUTOMATICALLY_BY_REFUND',
  CANCELLED_BY_PLAYERS_WITH_REFUND_ERROR = 'CANCELLED_BY_PLAYERS_WITH_REFUND_ERROR',
  CANCELLED_BY_ADMIN = 'CANCELLED_BY_ADMIN',
}

export enum RegistrationPaymentType {
  INDIVIDUAL = 'INDIVIDUAL',
  TEAM = 'TEAM',
}

export type Registration = {
  id: string;
  status: RegistrationStatus;
  paymentType: RegistrationPaymentType;
  /**
   * Payment channel used for this registration (e.g. {@link PaymentType.MERCADOPAGO}, {@link PaymentType.CASH}).
   * Optional: when omitted by the API, use the tournament's configured payment method for paid registrations.
   */
  paymentMethod?: PaymentType | null;
  tournament: string;
  team: Team;
  availability?: Availability[];
  /**
   * Emails of the players whose payment is already settled. Computed by the API; a payment the
   * manager marked and then undid drops out of this list.
   */
  payedBy?: string[];
  /**
   * Subset of {@link payedBy} whose payment the manager recorded by hand (the API's `registeredBy`).
   * Only these can be undone: a MercadoPago payment moves real money, and the cash a player handed
   * over at the club was not recorded by the manager either.
   */
  managerPayedBy?: string[];
  /** When the registration was created. Zone-less ISO string holding a UTC value. */
  createdAt?: string | null;
};

export interface GetTournamentsParams {
  page?: number;
  size?: number;
  sport?: number;
  gender?: Gender | 'ANY';
  startDate?: string;
  endDate?: string;
  radius?: number;
  coordinates?: string;
  isocode?: string;
  fields?: string;
}

export interface MatchResult {
  team1Score: number;
  team2Score: number;
  sportType?: string;
  setScores?: SetScore[];
  team1PenaltyScore?: number;
  team2PenaltyScore?: number;
  isValid: boolean;
  // Special result flags
  team1Walkover?: boolean;
  team2Walkover?: boolean;
}

export enum SpecialResultType {
  NONE = 'NONE',
  TEAM1_WALKOVER = 'TEAM1_WALKOVER',
  TEAM2_WALKOVER = 'TEAM2_WALKOVER',
  DOUBLE_WALKOVER = 'DOUBLE_WALKOVER',
}

export function getSpecialResultLabel(type: SpecialResultType): string {
  switch (type) {
    case SpecialResultType.TEAM1_WALKOVER:
      return 'Ganó por ausencia/abandono - Equipo 1';
    case SpecialResultType.TEAM2_WALKOVER:
      return 'Ganó por ausencia/abandono - Equipo 2';
    case SpecialResultType.DOUBLE_WALKOVER:
      return 'Doble walkover (ambos equipos con ausencia/abandono)';
    default:
      return '';
  }
}

export interface TeamStanding {
  teamId: string;
  played: number;
  won: number;
  lost: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  points: number;
}

export type FeeInfo = {
  fee: number;
  hasToPay: boolean;
};

export interface SetScore {
  team1Games: number;
  team2Games: number;
}
export enum SortOption {
  TEAM_ASC = 'team-asc',
  TEAM_DESC = 'team-desc',
  ELO_ASC = 'elo-asc',
  ELO_DESC = 'elo-desc',
  NAME_ASC = 'name-asc',
  NAME_DESC = 'name-desc',
}

export enum TeamGames {
  TEAM_1 = 'team1Games',
  TEAM_2 = 'team2Games',
}

export function getSortLabel(option: SortOption): string {
  switch (option) {
    case SortOption.TEAM_ASC:
      return 'Equipo (1-9)';
    case SortOption.TEAM_DESC:
      return 'Equipo (9-1)';
    case SortOption.ELO_ASC:
      return 'Elo (Menor a Mayor)';
    case SortOption.ELO_DESC:
      return 'Elo (Mayor a Menor)';
    case SortOption.NAME_ASC:
      return 'Nombre (A-Z)';
    case SortOption.NAME_DESC:
      return 'Nombre (Z-A)';
    default:
      return 'Ordenar por';
  }
}

export type FilterOption = 'all' | Gender.MALE | Gender.FEMALE | Gender.MIXED;

export interface TournamentSummary {
  tournamentCount: number;
  annualRevenue: number;
}
